import { useCallback, useState } from 'react';
import { Modal, Box, Button, Snackbar, Alert, Typography, Tooltip } from '@mui/material';

import { DataTable, type TableColumn, HandscoreIcon, Loading, ErrorMessage } from '@/src/ui';
import ScoreQuestionModal from './ScoreQuestionModal';
import { apiRequest } from '@/src/services';
import type { StudentExamAttemptDTO } from '../models/handscoreDTOs';
import { ReportHeaderStyle, ReportCellStyle, utcToLocal, formatOptions } from '@/src/utils';

interface ByStudentModalProps {
    open: boolean;
    onClose: () => void;
    updateQuestionCorrectStatus: (
        attemptQuestionId: number,
        studentUserId: number,
        isCorrect: boolean
    ) => void;
    studentExamAttemptId: number | null;
    studentExamAttempts: StudentExamAttemptDTO[];
}

export default function ByStudentModal({
    open,
    onClose,
    updateQuestionCorrectStatus,
    studentExamAttemptId,
    studentExamAttempts,
}: ByStudentModalProps) {
    const [error, setError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [activeSEAQuestionId, setActiveSEAQuestionId] = useState<number | null>(null);
    const [loadingQuestionId, setLoadingQuestionId] = useState<number | null>(null);

    const [scoreModalData, setScoreModalData] = useState<{
        questionId: number;
        questionType: string;
        studentUserId: number;
        isScored: boolean;
        isCorrect: boolean;
        attemptQuestionId: number;
        questionText: string;
        handscoreInstructions: string;
        submittedAnswer: string;
        questionAnswer: string | null;
    } | null>(null);

    const studentExamAttempt =
        studentExamAttempts.find((a) => a.attemptId === studentExamAttemptId) ?? null;

    const onActionClick = useCallback(
        async (row: any) => {
            if (scoreModalData?.attemptQuestionId === row.attemptQuestionId || !studentExamAttempt)
                return;

            setActiveSEAQuestionId(row.attemptQuestionId);
            setLoadingQuestionId(row.attemptQuestionId);

            setScoreModalData({
                questionId: row.questionId,
                questionType: row.questionType,
                studentUserId: studentExamAttempt.studentUserId,
                isScored: row.isScored,
                isCorrect: row.isCorrect,
                attemptQuestionId: row.attemptQuestionId,
                questionText: row.questionText,
                handscoreInstructions: row.handscoreInstructions,
                submittedAnswer: row.submittedAnswer,
                questionAnswer: null,
            });

            try {
                const result = await apiRequest(
                    'get',
                    `/assessments/studentExamAttemptQuestions/viewWithAnswer/${row.attemptQuestionId}`
                );

                setScoreModalData((prev) =>
                    prev
                        ? {
                              ...prev,
                              questionAnswer: (result.data as { answer: string }).answer,
                          }
                        : null
                );
            } catch (error) {
                console.error('Failed to load question preview:', error);
                setScoreModalData((prev) =>
                    prev ? { ...prev, questionAnswer: 'Error loading question' } : null
                );
            } finally {
                setLoadingQuestionId(null);
            }
        },
        [scoreModalData, studentExamAttempt]
    );

    const onHandscoreSave = useCallback(
        async (isCorrect: boolean, studentUserId: number, attemptQuestionId: number) => {
            if (!studentExamAttempt) return;
            try {
                await apiRequest(
                    'PUT',
                    `/assessments/studentExamAttemptQuestions/${attemptQuestionId}/handscoring`,
                    {
                        IsCorrect: isCorrect,
                        Student_UserId: studentUserId,
                        IsScored: true,
                    }
                );

                updateQuestionCorrectStatus(attemptQuestionId, studentUserId, isCorrect);

                studentExamAttempt.questions = studentExamAttempt.questions.map((q) =>
                    q.attemptQuestionId === attemptQuestionId ? { ...q, isScored: true } : q
                );

                setSuccess(true);
            } catch {
                setError(true);
            } finally {
                setScoreModalData(null);
                const firstUnscored = studentExamAttempt.questions.find(
                    (q) => q.isScored === false
                );
                if (firstUnscored) {
                    onActionClick(firstUnscored);
                } else {
                    setActiveSEAQuestionId(null);
                }
            }
        },
        [onActionClick, studentExamAttempt, updateQuestionCorrectStatus]
    );

    if (!studentExamAttempt) return null;

    const columns: TableColumn[] = [
        {
            dataKey: 'questionTitle',
            header: 'Question',
            align: 'left',
            allowSort: true,
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
        },
        {
            dataKey: 'questionType',
            header: 'Question Type',
            align: 'left',
            allowSort: true,
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
        },
        {
            dataKey: 'submittedAt',
            header: 'Submitted At',
            align: 'left',
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row) => {
                if (!row.submittedAt) return '-';
                return utcToLocal(row.submittedAt).toLocaleString(undefined, formatOptions);
            },
        },
        {
            dataKey: 'status',
            header: 'Status',
            align: 'left',
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row) => (
                <Box
                    component="span"
                    sx={{
                        display: 'inline-block',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        fontWeight: 500,
                        color: 'white',
                        bgcolor: row.isScored
                            ? row.isCorrect
                                ? 'success.main'
                                : 'var(--pm-g6)'
                            : 'warning.main',
                    }}
                >
                    {row.isScored ? 'Scored' : 'Unscored'}
                </Box>
            ),
        },
        {
            dataKey: 'action',
            header: 'Action',
            align: 'center',
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row) => (
                <Tooltip title="Handscore" arrow>
                    <Button
                        size="small"
                        className="icon-button"
                        startIcon={<HandscoreIcon style={{ width: '28px', height: '28px' }} />}
                        disabled={loadingQuestionId === row.attemptQuestionId}
                        onClick={() => onActionClick(row)}
                        sx={{
                            '&:hover': {
                                backgroundColor: 'transparent',
                            },
                        }}
                    ></Button>
                </Tooltip>
            ),
        },
    ];

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    width: '85%',
                    height: '75%',
                    maxHeight: '90%',
                    display: 'flex',
                    overflow: 'hidden',
                }}
            >
                {/* LEFT: Questions list */}
                <Box
                    sx={{
                        width: scoreModalData ? '60%' : '100%',
                        flexShrink: 0,
                        p: 2,
                        overflowY: 'auto',
                        transition: 'width 0.5s ease',
                    }}
                >
                    <Box
                        sx={{
                            display: 'inline-block',
                            px: 3,
                            py: 1,
                            borderRadius: '8px 8px 0 0',
                            bgcolor: 'var(--pm-secondary-light)',
                            color: 'var(--pm-dark)',
                            fontWeight: 600,
                            fontSize: '1rem',
                            ml: 2,
                            boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.4)',
                        }}
                    >
                        {studentExamAttempt.studentName} - {studentExamAttempt.examName}
                    </Box>
                    <br />

                    {/* Scrollable Table */}
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                        }}
                    >
                        <DataTable
                            data={studentExamAttempt.questions}
                            columns={columns}
                            tableName="StudentQuestions"
                            defaultSortColumn="questionTitle"
                            alternatingRowColor
                            rowSx={(row) =>
                                row.attemptQuestionId === activeSEAQuestionId
                                    ? { backgroundColor: 'rgba(255, 237, 123, 0.49)' }
                                    : {}
                            }
                        />
                        <Button onClick={onClose} sx={{ mt: 2 }}>
                            Close
                        </Button>
                    </Box>
                </Box>

                {/* RIGHT: Scoring panel with sticky instructions */}
                {scoreModalData && (
                    <Box
                        sx={{
                            width: '40%',
                            flexShrink: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            borderLeft: '1px solid #ddd',
                            height: '100%',
                        }}
                    >
                        {/* Sticky Handscore Instructions */}
                        <Box
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 10,
                                bgcolor: 'background.paper',
                                p: 2,
                                borderBottom: '1px solid #ddd',
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight="bold">
                                Handscore Instructions
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {scoreModalData.handscoreInstructions}
                            </Typography>
                        </Box>

                        {/* Scrollable ScoreQuestionModal */}
                        <Box
                            sx={{
                                flex: 1,
                                overflowY: 'auto',
                                p: 2,
                            }}
                        >
                            {scoreModalData.questionAnswer === null ? (
                                <>
                                    <Loading />
                                    <Button
                                        onClick={() => {
                                            setScoreModalData(null);
                                            setActiveSEAQuestionId(null);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </>
                            ) : scoreModalData.questionAnswer === 'Error loading question' ? (
                                <>
                                    <ErrorMessage error="Failed to load the correct answer. Please try again." />
                                    <Button
                                        onClick={() => {
                                            setScoreModalData(null);
                                            setActiveSEAQuestionId(null);
                                        }}
                                    >
                                        Back
                                    </Button>
                                </>
                            ) : (
                                <ScoreQuestionModal
                                    onClose={() => {
                                        setScoreModalData(null);
                                        setActiveSEAQuestionId(null);
                                    }}
                                    onSave={onHandscoreSave}
                                    questionId={scoreModalData.questionId}
                                    studentUserId={scoreModalData.studentUserId}
                                    isScored={scoreModalData.isScored}
                                    isCorrect={scoreModalData.isCorrect}
                                    attemptQuestionId={scoreModalData.attemptQuestionId}
                                    submittedAnswer={scoreModalData.submittedAnswer}
                                    questionAnswer={scoreModalData.questionAnswer}
                                />
                            )}
                        </Box>
                    </Box>
                )}

                {/* Snackbars */}
                <Snackbar
                    open={success}
                    autoHideDuration={3000}
                    onClose={() => setSuccess(false)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={() => setSuccess(false)}
                        severity="success"
                        sx={{ width: '100%' }}
                    >
                        Handscore saved successfully!
                    </Alert>
                </Snackbar>
                <Snackbar
                    open={error}
                    autoHideDuration={3000}
                    onClose={() => setError(false)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={() => setError(false)} severity="error" sx={{ width: '100%' }}>
                        Handscore not saved. Please try again.
                    </Alert>
                </Snackbar>
            </Box>
        </Modal>
    );
}
